/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, LinearProgress } from '@mui/material';
import { isEqual } from 'lodash';
import React from 'react';
import type { MosaicBranch, MosaicNode } from 'react-mosaic-component';
import { MosaicWindow } from 'react-mosaic-component';
import 'react-mosaic-component/react-mosaic-component.css';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayPluginWindows } from '@opengeoweb/plugins';
import { AlertBanner, sessionStorageProvider } from '@opengeoweb/shared';
import { genericActions } from '@opengeoweb/store';
import { getUniqueId } from './helpers';
import WorkspaceControls from './WorkspaceControls';
import { useResetViewPresetFiltersOnRoleChange } from '../../hooks/useResetViewPresetFiltersOnRoleChange';
import type { AppStore } from '../../store/types';
import {
  viewPresetActions,
  viewPresetSelectors,
} from '../../store/viewPresets';
import {
  defaultViewPresetsFilters,
  pluginViewPresetsFilter,
} from '../../store/viewPresets/reducer';
import type {
  UnregisterViewPresetPayload,
  ViewPresetsListFilter,
} from '../../store/viewPresets/types';
import { workspaceActions } from '../../store/workspace/reducer';
import {
  getViewById,
  getViewIds,
  getShouldPreventClose,
} from '../../store/workspace/selectors';
import type {
  WorkspaceComponentLookupPayload,
  WorkspaceLookupFunctionType,
  WorkspaceSetNewViewPayload,
} from '../../store/workspace/types';
import { defaultComponentsLookUp } from '../DefaultComponentsLookUp';
import { ViewPresetMenu } from '../ViewPresetMenu';
import type { ToggleViewPresetDialogProps } from '../WorkspaceViewTitle/WorkspaceViewTitleConnect';
import { WorkspaceViewTitleConnect } from '../WorkspaceViewTitle/WorkspaceViewTitleConnect';
import './WorkspaceView.css';

interface WorkspaceTileConnectProps {
  mosaicNodeId: string;
  path: MosaicBranch[];
  componentsLookUp: WorkspaceLookupFunctionType;
}

interface RegisterViewProps {
  panelId: string;
  mapPresetId: string;
}

const WorkspaceTileConnect: React.FC<WorkspaceTileConnectProps> = React.memo(
  ({
    mosaicNodeId = '',
    path,
    componentsLookUp,
  }: WorkspaceTileConnectProps) => {
    const dispatch = useDispatch();
    useResetViewPresetFiltersOnRoleChange(mosaicNodeId);

    const viewPresetsFilters = React.useMemo<ViewPresetsListFilter[]>(() => {
      const hasPluginsConfigured =
        !!sessionStorageProvider.getConfig()?.GW_PLUGIN_BACKEND_URL;
      if (hasPluginsConfigured) {
        return defaultViewPresetsFilters.flatMap((filter) =>
          filter.id === 'favourites'
            ? [pluginViewPresetsFilter, filter]
            : [filter],
        );
      }
      return defaultViewPresetsFilters;
    }, []);

    const view = useSelector((store: AppStore) =>
      getViewById(store, mosaicNodeId),
    );

    const viewIds: string[] = useSelector((store: AppStore) =>
      getViewIds(store),
    );

    const shouldPreventCloseView = useSelector((store: AppStore) =>
      getShouldPreventClose(store, mosaicNodeId),
    );

    const isViewPresetDialogOpen = useSelector((store: AppStore) =>
      viewPresetSelectors.getIsViewPresetListDialogOpen(store, mosaicNodeId),
    );

    const isViewPresetsFetching = useSelector((store: AppStore) =>
      viewPresetSelectors.getViewPresetsIsFetching(store, mosaicNodeId),
    );

    const errorViewPreset = useSelector((store: AppStore) =>
      viewPresetSelectors.getViewPresetDetailError(store, mosaicNodeId),
    );

    const addView = React.useCallback(
      (view: WorkspaceSetNewViewPayload): void => {
        dispatch(workspaceActions.setNewWorkspaceView(view));
      },
      [dispatch],
    );

    const registerView = React.useCallback(
      ({ panelId, mapPresetId }: RegisterViewProps): void => {
        dispatch(
          viewPresetActions.registerViewPreset({
            panelId,
            viewPresetId: mapPresetId,
            filters: viewPresetsFilters,
          }),
        );
      },
      [dispatch, viewPresetsFilters],
    );

    const unregisterView = React.useCallback(
      ({ panelId }: UnregisterViewPresetPayload): void => {
        dispatch(
          viewPresetActions.unregisterViewPreset({
            panelId,
          }),
        );
      },
      [dispatch],
    );

    const removeLinkedMap = React.useCallback(
      (panelId: string): void => {
        dispatch(genericActions.removeLinkedMap(panelId));
      },
      [dispatch],
    );

    const toggleViewPresetDialog = React.useCallback(
      ({
        panelId,
        isViewPresetDialogOpen,
      }: ToggleViewPresetDialogProps): void => {
        dispatch(
          viewPresetActions.toggleViewPresetListDialog({
            panelId,
            isViewPresetListDialogOpen: isViewPresetDialogOpen,
          }),
        );
      },
      [dispatch],
    );

    const createNode = React.useCallback((): MosaicNode<string> => {
      const newId = getUniqueId(viewIds);

      addView({
        componentType: 'Map',
        mosaicNodeId,
        newId,
        copyOriginalcontent: false,
      });

      return newId;
    }, [addView, mosaicNodeId, viewIds]);

    const title = view?.title || view?.id || mosaicNodeId;
    const pluginSourceKey = React.useMemo(() => {
      if (view?.componentType !== 'Plugin') {
        return undefined;
      }

      if (view?.id) {
        return `preset:${view.id}`;
      }

      const props = view.initialProps as
        | { id?: string; version?: string }
        | undefined;
      return `catalog:${props?.id ?? ''}:${props?.version ?? ''}`;
    }, [view]);

    React.useEffect(() => {
      const viewPresetId = view?.id || '';
      registerView({ panelId: mosaicNodeId, mapPresetId: viewPresetId });
      return (): void => {
        unregisterView({ panelId: mosaicNodeId });
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerView, unregisterView]);

    // generic componentsLookUp
    const genericComponentsLookupResult = React.useMemo(
      () =>
        componentsLookUp({
          title,
          id: mosaicNodeId,
          componentType: view?.componentType!,
          initialProps: view?.initialProps,
          // instanceKey is a plugin-specific concern kept in ComponentsLookUpPayload;
          // cast here to pass it without polluting the generic workspace contract
          instanceKey: pluginSourceKey,
        } as WorkspaceComponentLookupPayload),
      [
        componentsLookUp,
        mosaicNodeId,
        pluginSourceKey,
        title,
        view?.componentType,
        view?.initialProps,
      ],
    );

    // default componentsLookUp
    const defaultComponentsLookupResult = React.useMemo(
      () =>
        defaultComponentsLookUp({
          viewPresetId: view?.id!,
          componentType: view?.componentType!,
          mosaicNodeId,
        }),
      [mosaicNodeId, view?.componentType, view?.id],
    );

    return (
      <MosaicWindow<string>
        createNode={createNode}
        path={path}
        title={
          (
            <WorkspaceViewTitleConnect
              isViewPresetDialogOpen={isViewPresetDialogOpen || false}
              panelId={mosaicNodeId}
            />
          ) as unknown as string
        }
        toolbarControls={
          <WorkspaceControls
            createNode={createNode}
            panelId={mosaicNodeId}
            path={path}
            removeLinkedMap={removeLinkedMap}
            shouldPreventCloseView={shouldPreventCloseView}
          />
        }
      >
        {!!isViewPresetsFetching && (
          <LinearProgress
            color="secondary"
            data-testid="loading-bar"
            sx={{ position: 'absolute', width: '100%', top: 0, zIndex: 1001 }}
          />
        )}
        {!!errorViewPreset && (
          <Box sx={{ position: 'absolute', left: 0, right: 0, zIndex: 1002 }}>
            <AlertBanner shouldClose title={errorViewPreset} />
          </Box>
        )}
        {/** default componentsLookUp */}
        {defaultComponentsLookupResult}
        {/** generic componentsLookUp */}
        {genericComponentsLookupResult}
        <ViewPresetMenu
          closeMenu={(): void => {
            toggleViewPresetDialog({
              panelId: mosaicNodeId,
              isViewPresetDialogOpen: false,
            });
          }}
          isOpen={isViewPresetDialogOpen}
          panelId={mosaicNodeId}
        />
        <OverlayPluginWindows panelId={mosaicNodeId} />
      </MosaicWindow>
    );
  },
  isEqual,
);

export default WorkspaceTileConnect;
